import type { FC, MouseEventHandler } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { type Breadcrumb, useBreadcrumb } from "../../../components/Breadcrumb";
import { ButtonUI } from "../../../components/Button";
import { LoadingUI } from "../../../components/Loading";
import { PageTitle } from "../../../components/PageTitle";
import { ToggleUI } from "../../../components/Toggle";
import { SEARCH_LIMIT } from "../../../constants/search-fiilter";
import type { EmailSearchInput } from "../../../hooks/useEmailsRestApi";
import type {
  EmailsWithStatus,
  EmailWithStatus,
  SelectContextMenuHandler,
} from ".";
import { EmailsCardsUI } from "./components/EmailsCardsUI";
import { EmailsTableUI } from "./components/EmailsTableUI";
import { SearchEmailStats } from "./components/SearchEmailStats";
import { SearchEmailsFormUI } from "./components/SearchEmailsForm";
import { SearchFilterButton } from "./components/SearchFilterButton/SearchFilterButton";

type SearchEmailsPageProps = {
  isLoading: boolean;
  isMatter: boolean;
  currentSearchInput: EmailSearchInput;
  invisibleEmailNums: number;
  matterEmails: EmailsWithStatus;
  hrEmails: EmailsWithStatus;
  selectedEmail: EmailWithStatus;
  onSelectEmail: (id: string) => void;
  onToggleFavoriteEmail: (emailId: string, favorite: boolean) => void;
  onSelectContextMenu: SelectContextMenuHandler;
  onSearch: SubmitHandler<EmailSearchInput>;
  onClearFilter: () => void;
  onShowMore: MouseEventHandler;
};

const BREAD_CRUMBS: Breadcrumb[] = [
  {
    label: "ホーム",
    to: "/",
  },
  {
    label: "メール",
  },
  {
    label: "検索",
  },
];

// 選択したメールのID、親要素のスクロール位置を保持する変数
let scrollParentY: number | null = null;

const SearchEmailsPage: FC<SearchEmailsPageProps> = ({
  isLoading,
  isMatter,
  currentSearchInput,
  matterEmails,
  hrEmails,
  invisibleEmailNums,
  onToggleFavoriteEmail,
  onSelectEmail,
  onSelectContextMenu,
  onSearch,
  onClearFilter,
  onShowMore,
}) => {
  const { setBreadcrumbItems } = useBreadcrumb();

  // 表示形式の状態管理を追加
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  const viewModeLabel = viewMode === "card" ? "カード表示" : "テーブル表示";
  const activeEmails = useMemo(
    () => (isMatter ? matterEmails : hrEmails),
    [isMatter, matterEmails, hrEmails],
  );

  // 検索フィルターの表示/非表示状態管理（デフォルトは開いた状態）
  const [isSearchFilterOpen, setIsSearchFilterOpen] = useState(true);

  // フィルター開閉時に Layout コンテナを左に拡張（右端の位置は維持）
  useEffect(() => {
    const container = document.querySelector(
      "#layout-content-scroll > div",
    ) as HTMLElement | null;
    if (!container) return;
    if (isSearchFilterOpen) {
      container.classList.remove("max-w-7xl", "mx-auto");
      container.style.maxWidth = "calc((100% + 80rem) / 2)";
      container.style.marginLeft = "0";
      container.style.paddingLeft = "0";
    } else {
      container.classList.add("max-w-7xl", "mx-auto");
      container.style.maxWidth = "";
      container.style.marginLeft = "";
      container.style.paddingLeft = "";
    }
    return () => {
      container.classList.add("max-w-7xl", "mx-auto");
      container.style.maxWidth = "";
      container.style.marginLeft = "";
      container.style.paddingLeft = "";
    };
  }, [isSearchFilterOpen]);

  // 検索フィルターの表示/非表示を切り替え
  const handleToggleSearchFilter = useCallback(() => {
    setIsSearchFilterOpen(prev => !prev);
  }, []);

  // 検索フィルターを閉じる（バツボタン用）
  const handleCloseSearchFilter = useCallback(() => {
    setIsSearchFilterOpen(false);
  }, []);

  const favoriteCount = useMemo(
    () =>
      activeEmails.nodes.filter((node: EmailWithStatus) => node.favorite)
        .length,
    [activeEmails.nodes],
  );

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const rootDivRef = useRef<HTMLDivElement>(null);
  const scrollCardRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSelectedEmailId = queryParams.get("emailId");

  // 選択したメールのID、親要素のスクロール位置を保持する変数
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(
    initialSelectedEmailId || null,
  );

  // スクロール対象の親要素（layoutコンポーネント）を取得する関数
  const getScrollParentElement = useCallback((): HTMLElement | null => {
    if (!rootDivRef.current) return null;
    let el: HTMLElement | null = rootDivRef.current.parentElement;
    while (el) {
      if (el.id === "layout-content-scroll") {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  const handleSelectEmail = useCallback(
    (emailId: string): void => {
      // 親要素のスクロール位置を保存
      const parentElement = getScrollParentElement();
      scrollParentY = parentElement?.scrollTop ? parentElement.scrollTop : 0;

      // 既存のonSelectEmail呼び出し
      onSelectEmail(emailId);
    },
    [getScrollParentElement, onSelectEmail],
  );

  // スクロール位置の復元
  useEffect(() => {
    if (
      (isMatter && matterEmails.nodes.length > 0) ||
      (!isMatter && hrEmails.nodes.length > 0)
    ) {
      const parentElement = getScrollParentElement();
      if (
        scrollCardRef.current &&
        parentElement &&
        typeof scrollParentY === "number"
      ) {
        scrollCardRef.current.scrollIntoView();
        parentElement.scrollTop = scrollParentY;

        scrollParentY = null;
        setSelectedEmailId(null);

        const url = new URL(window.location.href);
        url.searchParams.delete("emailId");
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }, [
    getScrollParentElement,
    isMatter,
    matterEmails.nodes.length,
    hrEmails.nodes.length,
  ]);

  // コンテンツエリアの動的な高さ
  //  デフォルトはカード表示の高さ
  const [contentHeight, setContentHeight] = useState<string>(
    "calc(100vh - 200px)",
  );

  // ここでブラウザの高さ - ヘッダーの高さ を取得
  const windowHeight = window.innerHeight;
  const headerHeight = 102;
  const itemHeight = windowHeight - headerHeight;

  const searchFormRef = useRef<HTMLDivElement>(null);

  // ここでSearchEmailsFormUIもしくは SearchEmailStatsの親要素の高さを取得
  const [statsParentHeight, setStatsParentHeight] = useState<number>(0);

  useEffect(() => {
    const el = searchFormRef.current;
    if (!el) return;

    const updateHeight = () => {
      setStatsParentHeight(el.clientHeight);
    };

    updateHeight(); // 初回

    // レイアウト変化も拾う
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      viewMode === "card" &&
      isSearchFilterOpen &&
      statsParentHeight > itemHeight
    ) {
      // カード表示 && 検索フィルターが開いている && コンテンツエリアがフィルターより大きい
      setContentHeight(`${statsParentHeight - 56}px`);
      return;
    }
    if (
      viewMode === "table" &&
      isSearchFilterOpen &&
      statsParentHeight > itemHeight
    ) {
      // テーブル表示 && 検索フィルターが開いている && コンテンツエリアがフィルターより大きい
      setContentHeight(`${statsParentHeight - 56}px`);
      return;
    }

    if (viewMode === "table") {
      // テーブル表示 && 検索フィルターが閉じている
      setContentHeight("calc(100vh - 210px)");
      return;
    }
  }, [viewMode, isSearchFilterOpen, statsParentHeight, itemHeight]);

  return (
    <div ref={rootDivRef}>
      <PageTitle>メール検索</PageTitle>
      <div
        className={`flex transition-all duration-300 ${isSearchFilterOpen ? "gap-4" : ""}`}
      >
        <div
          className={`flex justify-center ${isSearchFilterOpen && !isLoading ? "w-[420px] h-full" : "w-0 opacity-0 h-0 overflow-hidden"} transition-all duration-300`}
        >
          <div className="w-full max-w-6xl px-4" ref={searchFormRef}>
            <SearchEmailsFormUI
              isLoading={isLoading}
              currentSearchInput={currentSearchInput}
              onSearch={onSearch}
              onClearFilter={onClearFilter}
              onClose={handleCloseSearchFilter}
              isSearchFilterOpen={isSearchFilterOpen}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="w-full flex justify-center pt-20">
            <LoadingUI type="Grid" />
          </div>
        ) : (
          <div className="flex flex-col flex-grow w-1/2">
            <div className="flex justify-center mb-4">
              <div className="w-full flex items-center justify-between">
                <div className="text-base-content flex items-end">
                  <SearchFilterButton
                    onClick={handleToggleSearchFilter}
                    isSearchFilterOpen={isSearchFilterOpen}
                  />
                  {activeEmails.nodes.length > 0 && (
                    <SearchEmailStats
                      totalCount={activeEmails.totalCount}
                      displayCount={activeEmails.nodes.length}
                      invisibleCount={invisibleEmailNums}
                      favoriteCount={favoriteCount}
                    />
                  )}
                </div>
                {activeEmails.nodes.length > 0 && (
                  <ToggleUI
                    label={viewModeLabel}
                    toggleSize="toggle-sm"
                    checked={viewMode === "table"}
                    onChange={event =>
                      setViewMode(event.target.checked ? "table" : "card")
                    }
                  />
                )}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-full overflow-auto">
                <div className="text-base-content">
                  <div
                    className="border-b border-gray-200 rounded-xl"
                    style={{ height: contentHeight }}
                  >
                    {viewMode === "card" ? (
                      <EmailsCardsUI
                        isMatter={isMatter}
                        emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                        onSelectEmail={handleSelectEmail}
                        onToggleFavoriteEmail={onToggleFavoriteEmail}
                        currentSearchInput={currentSearchInput}
                        scrollCardRef={scrollCardRef}
                        scrollCardId={selectedEmailId}
                      />
                    ) : (
                      <EmailsTableUI
                        isMatter={isMatter}
                        emails={isMatter ? matterEmails.nodes : hrEmails.nodes}
                        onSelectEmail={onSelectEmail}
                        onSelectContextMenu={onSelectContextMenu}
                        onToggleFavoriteEmail={onToggleFavoriteEmail}
                        freeWords={currentSearchInput.freeWords || []}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {((isMatter && matterEmails.totalCount > SEARCH_LIMIT) ||
              (!isMatter && hrEmails.totalCount > SEARCH_LIMIT)) && (
              <div className="flex justify-center space-x-5">
                <ButtonUI className="w-40 mb-20" onClick={onShowMore}>
                  もっと見る
                </ButtonUI>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default SearchEmailsPage;
